import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface TvCard {
  id: number;
  title: string;
  coverImage: string;
  rating: number;
  updateInfo: string;
  category: string;
}

interface TvGridProps {
  title: string;
  items: TvCard[];
  viewAllLink?: string;
  accent?: boolean;
}

export default function TvGrid({ title, items, viewAllLink, accent }: TvGridProps) {
  const { t } = useTranslation();

  return (
    <section className="py-8 md:py-10">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg md:text-xl font-bold text-foreground-900">
            {accent && <i className="ri-fire-line mr-2 text-accent-500"></i>}
            {!accent && <i className="ri-flashlight-line mr-2 text-primary-500"></i>}
            {title}
          </h2>
          {viewAllLink && (
            <Link to={viewAllLink} className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
              {t('home:viewAll')} <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {items.map((item) => (
            <Link
              key={item.id}
              to={`/article/${item.id}`}
              className="group cursor-pointer"
            >
              <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-2.5">
                <img
                  src={item.coverImage}
                  alt={item.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                  loading="lazy"
                />
                <div className="absolute top-2 left-2">
                  <span className="px-1.5 py-0.5 bg-primary-500 text-white text-[10px] font-bold rounded">
                    {item.rating}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                  <span className="text-[10px] text-white/80">{item.updateInfo}</span>
                </div>
              </div>
              <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                {item.title}
              </h3>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}